/**
 * TwelveDataProxySocket
 * ---------------------
 * Drop-in replacement for a direct Twelve Data WS client.
 * Connects to the backend raw WS gateway (/prices) which fans out a
 * single upstream Twelve Data connection to all clients.
 *
 * Outbound message format (sent to backend):
 *   { action: "subscribe",   symbols: ["EUR/USD", "XAU/USD"] }
 *   { action: "unsubscribe", symbols: ["EUR/USD"] }
 *
 * Inbound tick format (matches other providers already in use):
 *   {
 *     data: {
 *       s: "EUR/USD",   // symbol
 *       c: 1.0823,      // current price
 *       o: 1.0800,      // open  (null if unavailable per tick)
 *       h: 1.0850,      // high  (null if unavailable per tick)
 *       l: 1.0790,      // low   (null if unavailable per tick)
 *       P: 0.21         // change% (null if unavailable per tick)
 *     }
 *   }
 */

import consts from "../constant"

const safeJsonParse = (raw) => {
  try {
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

const toFiniteNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}

const deriveChangePercent = (price, open) => {
  if (!Number.isFinite(price) || !Number.isFinite(open) || open === 0) return NaN
  return ((price - open) / open) * 100
}

export class TwelveDataProxySocket {
  /**
   * @param {object}   options
   * @param {Function} options.onUpdate  callback(symbol, tickData)
   * @param {number}   [options.reconnectDelayMs=2000]
   * @param {number}   [options.maxReconnectDelayMs=30000]
   */
  constructor({ onUpdate, reconnectDelayMs = 2000, maxReconnectDelayMs = 30_000 } = {}) {
    let defaultWsUrl = "ws://localhost:3005/prices";
    if (typeof window !== "undefined" && window.location && window.location.hostname) {
      const host = window.location.hostname;
      if (host.includes("playez5050.manavtechlabs.in") || host.includes("manavtechlabs.in") || host.includes("ez5050.com")) {
        defaultWsUrl = "wss://apiez5050.manavtechlabs.in/prices";
      } else {
        const port = "3005";
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        defaultWsUrl = `${protocol}//${host}:${port}/prices`;
      }
    }
    this.backendWsUrl = defaultWsUrl;
    this.onUpdate = onUpdate
    this.reconnectDelayMs = reconnectDelayMs
    this.maxReconnectDelayMs = maxReconnectDelayMs

    /** @type {WebSocket|null} */
    this.socket = null
    this.isCancelled = false
    this.reconnectAttempts = 0

    /** Symbols this instance manages — preserved across reconnects */
    this.subscribedSymbols = new Set()

    /** Callbacks registered per symbol: symbol → Set<fn> */
    this.listeners = new Map()
  }

  // -------------------------------------------------------------------------
  // Connection
  // -------------------------------------------------------------------------

  connect() {
    if (this.isCancelled) return

    this.socket = new WebSocket(this.backendWsUrl)

    this.socket.onopen = () => {
      console.log('[TwelveDataProxy] Connected to backend gateway')
      this.reconnectAttempts = 0
      this._resubscribeAll()
    }

    this.socket.onmessage = (event) => {
      const payload = safeJsonParse(event.data)
      const data = payload?.data
      if (!data || typeof data.s !== 'string') return

      const symbol = data.s
      const nextPrice = toFiniteNumber(data.c)
      const nextOpenPrice = toFiniteNumber(data.o)
      const nextHighPrice = toFiniteNumber(data.h)
      const nextLowPrice = toFiniteNumber(data.l)
      const rawChangePercent = toFiniteNumber(data.P)
      const nextChangePercent = Number.isFinite(rawChangePercent)
        ? rawChangePercent
        : deriveChangePercent(nextPrice, nextOpenPrice)

      const tick = {
        price: nextPrice,
        openPrice: nextOpenPrice,
        highPrice: nextHighPrice,
        lowPrice: nextLowPrice,
        changePercent: nextChangePercent
      }

      // Global callback (same as your broadcastUpdate pattern)
      this.onUpdate?.(symbol, tick)

      // Per-symbol listeners
      const cbs = this.listeners.get(symbol)
      if (cbs) cbs.forEach((cb) => cb(tick))
    }

    this.socket.onerror = () => {
      this.socket?.close()
    }

    this.socket.onclose = () => {
      if (!this.isCancelled) this._scheduleReconnect()
    }
  }

  disconnect() {
    this.isCancelled = true
    this.socket?.close()
    this.socket = null
  }

  // -------------------------------------------------------------------------
  // Subscription management
  // -------------------------------------------------------------------------

  /**
   * Subscribe to one or more symbols.
   * Call before or after connect() — will flush on reconnect automatically.
   * @param {string|string[]} symbols
   */
  subscribe(symbols) {
    const list = (Array.isArray(symbols) ? symbols : [symbols]).map((s) =>
      String(s).toUpperCase()
    )
    list.forEach((s) => this.subscribedSymbols.add(s))
    this._send({ action: 'subscribe', symbols: list })
  }

  /**
   * Unsubscribe from one or more symbols.
   * @param {string|string[]} symbols
   */
  unsubscribe(symbols) {
    const list = (Array.isArray(symbols) ? symbols : [symbols]).map((s) =>
      String(s).toUpperCase()
    )
    list.forEach((s) => this.subscribedSymbols.delete(s))
    this._send({ action: 'unsubscribe', symbols: list })
  }

  /**
   * Register a per-symbol tick listener.
   * Returns a cleanup function.
   * @param {string}   symbol
   * @param {Function} cb
   * @returns {Function} unregister
   */
  onSymbol(symbol, cb) {
    const key = String(symbol).toUpperCase()
    if (!this.listeners?.has(key)) this.listeners.set(key, new Set())
    this.listeners?.get(key).add(cb)
    return () => this.listeners.get(key)?.delete(cb)
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  _send(msg) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg))
    }
    // If not yet connected, _resubscribeAll() will flush on open
  }

  _resubscribeAll() {
    if (this.subscribedSymbols.size === 0) return
    this._send({
      action: 'subscribe',
      symbols: Array.from(this.subscribedSymbols)
    })
  }

  _scheduleReconnect() {
    this.reconnectAttempts++
    const delay = Math.min(
      this.reconnectDelayMs * this.reconnectAttempts,
      this.maxReconnectDelayMs
    )
    console.log(`[TwelveDataProxy] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
    setTimeout(() => this.connect(), delay)
  }
}
